<template>
  <div>
    <section>
      <b-col class="d-flex justify-content-end px-0 pt-25">
        <filter-wrapper
          :properties="selectedFilters"
          :width-offset="400"
          @clicked="openSelectedFilter"
          @cleared="clearFilters"
        />
        <dropdown
          :title="$tk('ClientPortal.MasterTable.Filters')"
          class="mb-1 mr-1"
          :disabled="isLoading"
          :show-dropdown="showFilters"
          @change="toggleFilters"
        >
          <template #menu-items>
            <dropdown-submenu
              v-for="(filter, index) in model.filters"
              :key="filter.name + '-' + index"
              :input-id="filter.name"
              :text="filter.name"
              :selected-filter="selectedFilter(filter.name)"
              input-name="submenu"
              :checked="menu"
              @changeSelectedMenu="changeMenu"
            >
              <template #items>
                <dropdown-item v-if="filter.name !== 'Statuses'">
                  <b-form-input
                    v-if="filter.name === 'ProfitPercent'"
                    v-model="model.serverParams.columnFilters[firstLetterToLower(filter.name)]"
                    class="__dropdown-input"
                    type="number"
                    :formatter="maxMinPercent"
                    :placeholder="filter.name"
                    :disabled="onChange"
                    @input="onInputChange(filter.name)"
                    @keypress="preventDot"
                  />
                  <b-form-input
                    v-else
                    v-model="model.serverParams.columnFilters[firstLetterToLower(filter.name)]"
                    class="__dropdown-input"
                    :disabled="onChange"
                    :placeholder="filter.name"
                    @input="onInputChange(filter.name)"
                  />
                </dropdown-item>
                <template v-if="filter.name==='Statuses'">
                  <dropdown-item
                    v-for="(status, statusIndex) in model.statuses"
                    :key="status + '-' + statusIndex"
                    :label="status.text"
                    :filter-name="filter.name"
                    :value="status.value"
                    :menu-name="filter.name"
                    :selected="status.selected"
                    @change="includeOrExclude"
                  />
                </template>
                <template v-else>
                  <dropdown-item
                    v-for="(option, optionIndex) in filter.operators"
                    :key="option + '-' + optionIndex"
                    type="radio"
                    :label="option"
                    :filter-name="filter.name"
                    :selected="filter.selected"
                    :value="option"
                    :menu-name="filter.name"
                    @change="includeOrExclude"
                  />
                </template>
              </template>
              <template #footer>
                <dropdown-clear-item
                  :title="$tk('ClientPortal.Filters.Clear')"
                  @click="clearFilters(filter.name, false)"
                />
              </template>
            </dropdown-submenu>
            <dropdown-clear-item
              :title="$tk('ClientPortal.Filters.ClearAll')"
              @click="clearFilters"
            />
          </template>
        </dropdown>
        <SearchInputField
          :disabled="isLoading"
          :placeholder="$tk('Admin.Withdraws.SearchByPropertyName')"
          @onInputSearch="onSearchChange"
        />
      </b-col>
      <b-card
        class="my-50 admin-table--management"
        no-body
      >
        <Table
          :data="allProperties"
          :columns="propertiesColumns"
          :is-loading="isLoading"
          remote-mode
          class="admin-table__data"
          type-of-table="properties"
          @changePage="changeCurrentPage"
          @onSortChange="sort"
        />
      </b-card>
    </section>
    <b-modal
      id="modal-edit-property"
      :title="$tk('ClientPortal.Admin.Property.EditProperty')"
      :ok-title="$tk('ClientPortal.Form.Actions.Save')"
      :cancel-title="$tk('ClientPortal.Form.Actions.Cancel')"
      :ok-disabled="isFormComplete()"
      centered
      no-enforce-focus
      cancel-variant="outline-secondary"
      @show="getPropertyModel"
      @ok="updateProperty"
    >
      <b-form-group
        class="mb-2"
        :label="$tk('ClientPortal.Admin.Property.Name')"
        :invalid-feedback="errors['Name']"
      >
        <b-form-input
          v-model="propertyModel.name"
          :placeholder="$tk('ClientPortal.Admin.Property.Name')"
          type="text"
          :state="errors['Name'] == null ? null : false"
        />
      </b-form-group>
      <b-form-group
        class="mb-2"
        :label="$tk('ClientPortal.Admin.Property.PropertyUrl')"
        :invalid-feedback="errors['Url']"
      >
        <b-form-input
          v-model="propertyModel.url"
          :placeholder="$tk('ClientPortal.Admin.Property.PropertyUrl')"
          type="text"
          :state="errors['Url'] == null ? null : false"
        />
      </b-form-group>
      <b-form-group
        class="mb-2"
        :label="$tk('ClientPortal.Admin.Property.ProfitPercent')"
        :invalid-feedback="errors['ProfitPercent']"
      >
        <b-form-input
          v-model="propertyModel.profitPercent"
          :placeholder="$tk('ClientPortal.Admin.Property.ProfitPercent')"
          type="text"
          :state="errors['ProfitPercent'] == null ? null : false"
        />
      </b-form-group>
      <b-form-group class="mb-0">
        <div
          v-for="status in allPropertiesStatuses"
          :key="status.value"
        >
          <b-form-radio
            v-model.number="propertyModel.statusId"
            class="mb-50"
            :value="status.value"
          >
            <div class="text-dark mb-50">
              {{ status.text }}
            </div>
          </b-form-radio>
        </div>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import {
  computed,
  onMounted,
  ref,
  reactive,
} from '@vue/composition-api'
import store from '@/store'
import Table from '@/components/Table.vue'
import SearchInputField from '@/components/SearchInputField.vue'
import notification from '@/mixins/toastification'
import PropertiesService from '@/api/admin/properties.service'
import Dropdown from '@/components/Dropdown.vue'
import DropdownItem from '@/components/DropdownItem.vue'
import DropdownSubmenu from '@/components/DropdownSubmenu.vue'
import DropdownClearItem from '@/components/DropdownClearItem.vue'
import FilterWrapper from '@/components/FilterWrapper.vue'
import { debounce } from 'vue-debounce'
import translation from '@/mixins/translate'
import onSortChange from '@/mixins/onSortChange'

export default {
  components: {
    Table,
    SearchInputField,
    Dropdown,
    DropdownItem,
    DropdownSubmenu,
    FilterWrapper,
    DropdownClearItem,
  },
  mixins: [notification],
  setup() {
    const propertiesColumns = [
      {
        label: translation.methods.$tk('ClientPortal.Property.Id'),
        field: 'id',
        width: '100px !important',
        sortable: false,
      },
      {
        label: translation.methods.$tk('ClientPortal.Property.AccountName'),
        field: 'accountName',
        width: '250px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Property.Name'),
        field: 'name',
        width: '250px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Property.Url'),
        field: 'url',
        width: '420px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Property.ProfitPercent'),
        field: 'profitPercent',
        width: '150px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Property.ApplicationDate'),
        field: 'applicationDate',
        width: '200px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Property.Status'),
        field: 'status',
        width: '200px !important',
      },
    ]
    const allProperties = computed(() => store.getters.allProperties)
    const model = computed(() => store.getters.getPropertiesModel)
    const allPropertiesStatuses = computed(() => store.getters.allPropertiesStatuses)
    const propertyModel = ref({})
    const { pageLength } = allProperties.value
    const isLoading = ref(false)
    const errors = ref({})
    const menu = ref('')
    const showFilters = ref(false)
    const selectedFilters = reactive([])
    const onChange = ref(false)

    onMounted(async () => {
      isLoading.value = true
      await store.dispatch('getProperties')
      isLoading.value = false
    })
    /* eslint-disable no-nested-ternary, arrow-body-style */
    const maxMinPercent = value => {
      if (!value) {
        return ''
      }
      return value > 100 ? 100 : (value < 1 ? 0 : parseInt(value, 10))
    }
    const preventDot = keyboardEvent => {
      const keycode = keyboardEvent.charCode || keyboardEvent.keyCode
      if (keycode === 46 || keycode === 101) {
        keyboardEvent.preventDefault()
      }
    }
    const onFilterChange = async () => {
      onChange.value = true
      if (model.value.serverParams.columnFilters.profitPercent || model.value.serverParams.columnFilters.profitPercent === 0) {
        model.value.serverParams.columnFilters.profitPercent = model.value.serverParams.columnFilters.profitPercent.toString()
        model.value.serverParams.propertyFilters.profitPercent.value = model.value.serverParams.propertyFilters.profitPercent.value.toString()
      }
      await store.dispatch('getFilteredProperties', model.value)
      onChange.value = false
    }

    const onSearchChange = async value => {
      model.value.serverParams.name = value
      if (value === '' || value.trim().length === 0) {
        model.value.serverParams.name = null
      }
      await store.dispatch('getFilteredProperties', model.value)
    }

    const changeCurrentPage = async val => {
      model.value.serverParams.page = val
      await store.dispatch('getFilteredProperties', model.value)
    }
    const sort = value => {
      onSortChange(value, model, 'getFilteredProperties')
    }

    const getPropertyModel = () => {
      propertyModel.value = store.getters.getPropertiesModel
      errors.value = {}
    }
    const changeMenu = newOpenedMenu => {
      menu.value === newOpenedMenu ? menu.value = '' : menu.value = newOpenedMenu
    }
    const firstLetterToLower = text => text && text[0].toLowerCase() + text.slice(1)

    const updateSelectedFilters = (filter, count) => {
      const filterObj = selectedFilters.find(f => f.name === filter)
      if (filterObj) {
        if (count > 0) { filterObj.selectedFilters = count } else {
          const filterIndex = selectedFilters.findIndex(f => f.name === filter)
          selectedFilters.splice(filterIndex, 1)
        }
      } else {
        selectedFilters.push({ name: filter, selectedFilters: count })
      }
    }

    const updateStatuses = (filter, value) => {
      const { propertyFilters } = model.value.serverParams
      const selectedFilter = propertyFilters[filter]
      const optionIndex = selectedFilter.value.indexOf(value)
      const statusSelected = model.value.statuses.find(status => status.value === value)
      const { columnFilters } = model.value.serverParams
      if (optionIndex < 0) {
        selectedFilter.value.push(value)
        statusSelected.selected = true
        columnFilters[filter].push(value)
      } else {
        selectedFilter.value.splice(optionIndex, 1)
        statusSelected.selected = false
        columnFilters[filter].splice(optionIndex, 1)
        if (selectedFilter.value.length === 0 && columnFilters[filter].length === 0) {
          delete propertyFilters[filter]
          delete columnFilters[filter]
        }
      }
    }

    const updatePropertyFilter = (filter, value, operator) => {
      const { propertyFilters } = model.value.serverParams
      const selectedFilter = propertyFilters[filter]
      if (selectedFilter.operator === operator) {
        delete propertyFilters[filter]
      } else {
        selectedFilter.operator = operator
        selectedFilter.value = value.toString()
      }
    }

    const updatePropertyFilters = (filter, value, operator = null) => {
      const { propertyFilters } = model.value.serverParams
      const filterObject = firstLetterToLower(filter)
      let count
      if (filterObject in propertyFilters) {
        if (filter === 'Statuses') {
          updateStatuses(filterObject, value)
        } else {
          updatePropertyFilter(filterObject, value, operator)
        }
      } else {
        propertyFilters[filterObject] = {
          operator,
          property: filter,
          value: filter === 'Statuses' ? [value] : ((filter === 'ProfitPercent' && !value) ? '1' : value),
        }
        if (filter === 'Statuses') {
          const { columnFilters } = model.value.serverParams
          columnFilters[filterObject] = []
          columnFilters[filterObject].push(value)
        }
      }
      if (propertyFilters[filterObject]) {
        count = filter === 'Statuses' ? propertyFilters[filterObject].value.length : 1
      }
      updateSelectedFilters(filter, count)
    }

    const includeOrExclude = (filter, option) => {
      if (filter === 'Statuses') {
        const statusSelected = model.value.statuses.find(status => status.value === option)
        statusSelected.selected === true ? statusSelected.selected = false : statusSelected.selected = true
        updatePropertyFilters(filter, option)
        onFilterChange()
      } else {
        const selectedOption = model.value.filters.find(f => f.name === filter)
        const { columnFilters } = model.value.serverParams
        selectedOption.selected === option ? selectedOption.selected = '' : selectedOption.selected = option
        updatePropertyFilters(filter, columnFilters[firstLetterToLower(filter)], option)
        if (!columnFilters[firstLetterToLower(filter)].toString()) { return }
        onFilterChange()
      }
    }

    const toggleFilters = (toogleFilter = null) => {
      menu.value = ''
      showFilters.value = toogleFilter ?? !showFilters.value
    }

    const clearFilters = (filter = null, closeAllDropdowns = true) => {
      const { propertyFilters } = model.value.serverParams
      const { columnFilters } = model.value.serverParams
      const { statuses } = model.value
      const { filters } = model.value
      if (!filter) {
        selectedFilters.splice(0, selectedFilters.length)
        filters.forEach(f => { f.selected = '' })
        Object.keys(propertyFilters).forEach(propertyFilter => { delete propertyFilters[propertyFilter] })
        Object.keys(columnFilters).forEach(columnFilter => { columnFilters[columnFilter] = null })
        model.value.statuses.forEach(status => { status.selected = false })
        toggleFilters(false)
      } else {
        const selectedFilter = model.value.filters.find(f => f.name === filter)
        const includedFilterIndex = selectedFilters.findIndex(f => f.name === filter)
        const filterObject = firstLetterToLower(filter)
        columnFilters[filterObject] = null
        selectedFilters.splice(includedFilterIndex, 1)
        selectedFilter.selected = ''
        delete propertyFilters[filterObject]
        if (filter === 'Statuses') { statuses.forEach(status => { status.selected = false }) }
        if (closeAllDropdowns) { toggleFilters(false) }
      }
      onFilterChange()
      menu.value = ''
    }

    const onInputChange = debounce(async filter => {
      const filterObject = firstLetterToLower(filter)
      const { propertyFilters } = model.value.serverParams
      const { columnFilters } = model.value.serverParams
      if (filterObject in propertyFilters) {
        propertyFilters[filterObject].value = columnFilters[filterObject]
      } else { return }
      if (filterObject === 'profitPercent' && propertyFilters[filterObject].value === '') {
        const selectedFilter = model.value.filters.find(f => f.name === filter)
        const includedFilterIndex = selectedFilters.findIndex(f => f.name === filter)
        columnFilters[filterObject] = null
        selectedFilters.splice(includedFilterIndex, 1)
        selectedFilter.selected = ''
        delete propertyFilters[filterObject]
        if (propertyFilters.length === 0) {
          await store.dispatch('getProperties')
        } else {
          onFilterChange()
        }
      }
      onFilterChange()
    }, 500)

    const isFormComplete = () => !propertyModel.value.profitPercent

    const updateProperty = async bvModalEvt => {
      bvModalEvt.preventDefault()
      try {
        const response = await PropertiesService.updateProperties(propertyModel.value)
        if (response.saved) {
          bvModalEvt.vueTarget.hide('modal-edit-property')
          notification.methods.success(response.message)
        } else {
          notification.methods.error(response.message)
          errors.value = response.errors
        }
      } catch (err) {
        notification.methods.error(err.message)
      } finally {
        await store.dispatch('getFilteredProperties', model.value)
      }
    }

    const selectedFilter = name => {
      const filter = selectedFilters.findIndex(f => f.name === name)
      if (filter > -1) return true
      return false
    }

    const openSelectedFilter = filterName => {
      toggleFilters(true)
      menu.value = filterName
    }

    window.onclick = e => {
      if (!e.path.find(x => x.className?.includes('dropdown'))) {
        toggleFilters(false)
        menu.value = ''
      }
    }
    return {
      allProperties,
      isLoading,
      maxMinPercent,
      changeCurrentPage,
      pageLength,
      allPropertiesStatuses,
      propertyModel,
      updateProperty,
      getPropertyModel,
      changeMenu,
      showFilters,
      selectedFilters,
      model,
      onSearchChange,
      menu,
      includeOrExclude,
      clearFilters,
      toggleFilters,
      onInputChange,
      firstLetterToLower,
      errors,
      isFormComplete,
      propertiesColumns,
      sort,
      selectedFilter,
      openSelectedFilter,
      onChange,
      preventDot,
    }
  },
}
</script>
